import {fullDate, shiftDate, shortDate, ymd} from '../../../src/utils/dates.ts';
import {sortByGroupByValue} from '../../../src/utils/sort.ts';
import {createElement} from '../../lib/dom.ts';
import {getLanguage} from '../../lib/i18n.ts';
import {ContentSourceEdition} from '../../models/content-source-edition.ts';
import {DigitalEdition} from '../../models/digital-edition.ts';
import type {ReaderEdition} from '../../models/reader-edition.ts';
import {ReelsEdition} from '../../models/reels-edition.ts';
import type {Listings} from './lib/listings.ts';
import type {ReaderListing} from './reader-listing.ts';

const LANGUAGE_TO_LOCALE: Record<string, string> = {
	en: 'en-GB',
	no: 'nb-NO',
	da: 'da-DK',
	de: 'de-DE'
};

const sectionMap: Record<string, string> = {
	LEDE: 'Leder',
	NYHE: 'Nyheter',
	UTLANDIN: 'Utland',
	UTLA: 'Utland',
	SPORTINN: 'Sport',
	SPOR: 'Sport',
	KULTINN: 'Kultur',
	KULT: 'Kultur',
	DEBAINN: 'Debatt',
	DEBA: 'Debatt',
	'BY-FOLKINN': 'By & folk',
	'No section': 'No section'
};

const groupSortMap: Record<string, number> = {
	Nyheter: 1,
	nyheter: 1,
	børs: 3,
	investor: 4,
	Sport: 5,
	Debatt: 8,
	meninger: 8,
	Kultur: 12,
	'Krim / Ulykke': 15,
	Vin: 20
};

export function renderToc(tocListEl: HTMLElement, listings: Listings, readerEdition: ReaderEdition) {
	tocListEl.innerHTML = `
<div class="toc__edition-header">
	<a class="toc__product-name" href="">${readerEdition.productTitle}</a>
	<div class="toc__edition-name">${readerEdition.editionName}</div>
	<div class="toc__publish-date">${readerEdition.publishDate ? fullDate(readerEdition.publishDate, readerEdition.locale) : ''}</div>
	<div class="toc__toggle-menu" data-action="toggle-menu" data-action-value="show">≡</div>
	<div class="toc__toggle-menu" data-action="toggle-menu" data-action-value="hide">×</div>
</div>
<div class="toc__toggle-buttons">
	<button class="button toc__hide-done" data-action="toggle-show-done">Hide done</button>
	<button class="button toc__reset-state" data-action="reset-state">Reset</button>
</div>
<div class="toc__placement-buttons">
	<button class="button toc__placement-left" data-action="set-toc-placement" data-action-value="left">Left</button>
	<button class="button toc__placement-below" data-action="set-toc-placement" data-action-value="below">Below</button>
</div>
<div class="toc__entries"></div>
`;
	const tocEntriesEl = tocListEl.querySelector('.toc__entries') as HTMLElement;
	const previousEditionsHeaderEl = createElement(tocListEl.ownerDocument, 'div', ['toc__previous-editions-header']);
	previousEditionsHeaderEl.innerHTML = 'Previous editions';
	const editionLinks = [];

	listings.forEach((listing) => {
		listing.renderTocEntry();
	});

	if (readerEdition instanceof ContentSourceEdition) {
		const groups = assignToGroups(listings);
		groups.forEach(({label, listings}) => {
			if (!listings.length) {
				return;
			}
			tocEntriesEl.appendChild(createSectionHeader(label));

			listings.forEach((listing: ReaderListing) => {
				tocEntriesEl.appendChild(listing.tocEntryEl);
			});
		});

		if (ymd(readerEdition.publishDate, readerEdition.timezone) !== ymd(new Date(), readerEdition.timezone)) {
			tocEntriesEl.appendChild(createDateLink(new Date(), "Today's edition") as HTMLElement);
		}
		let offset = 0;
		while (offset > -7) {
			offset--;
			const linkEl = createRelativeDateLink(readerEdition.publishDate, offset);
			if (linkEl instanceof HTMLElement) {
				editionLinks.push(linkEl);
			}
		}
	} else if (readerEdition instanceof DigitalEdition && readerEdition.productType === 'frontpage') {
		let groupBy = '';
		listings.forEach((listing: ReaderListing) => {
			const groupByValue = getGroupByValue(listing);
			if (groupBy && !groupByValue.label && tocEntriesEl.lastElementChild) {
				tocEntriesEl.lastElementChild.classList.add('toc-entry--last-in-group');
				groupBy = '';
			}
			if (groupByValue.label && groupByValue.label !== groupBy) {
				groupBy = groupByValue.label;
				tocEntriesEl.appendChild(createSectionHeader(groupBy));
			}
			tocEntriesEl.appendChild(listing.tocEntryEl);
		});
	} else if (readerEdition instanceof ReelsEdition) {
		listings.forEach((listing: ReaderListing) => {
			tocEntriesEl.appendChild(listing.tocEntryEl);
		});
	} else {
		let groupBy = '';
		listings.forEach((listing: ReaderListing) => {
			const groupByValue = getGroupByValue(listing);
			if (groupByValue.label && groupByValue.label.toLowerCase() !== groupBy.toLowerCase()) {
				groupBy = groupByValue.label;
				tocEntriesEl.appendChild(createSectionHeader(groupBy));
			}
			tocEntriesEl.appendChild(listing.tocEntryEl);
		});
	}

	if (editionLinks.length) {
		tocEntriesEl.appendChild(previousEditionsHeaderEl);
		tocEntriesEl.append(...editionLinks);
	}

	const leftScrollIndicator = createElement(tocListEl.ownerDocument, 'div', ['left-scroll-indicator']);
	const rightScrollIndicator = createElement(tocListEl.ownerDocument, 'div', ['right-scroll-indicator']);
	tocEntriesEl.append(leftScrollIndicator, rightScrollIndicator);

	function createSectionHeader(groupBy: string) {
		const sectionHeaderEl = createElement(tocListEl.ownerDocument, 'div', 'toc-section-header');
		sectionHeaderEl.innerText = groupBy;
		sectionHeaderEl.dataset.sectionName = groupBy.toLocaleLowerCase();
		return sectionHeaderEl;
	}

	function createRelativeDateLink(startDate: string, days: number) {
		if (!(readerEdition instanceof ContentSourceEdition)) {
			return;
		}
		const date = shiftDate(new Date(startDate), days);
		return createDateLink(date);
	}

	function createDateLink(date: Date, label = '') {
		if (!(readerEdition instanceof ContentSourceEdition)) {
			return;
		}
		const dateLinkEl = createElement(tocListEl.ownerDocument, 'div', 'toc-edition-link');
		dateLinkEl.innerHTML = `<a href="${readerEdition.getUrl(date)}">${label || shortDate(date, readerEdition.locale)}</a>`;
		return dateLinkEl;
	}

	function assignToGroups(listings: Listings) {
		const sortOrder = 'desc';
		const groupsIndex: Record<
			string,
			{sort: number | string | [string, number]; label: string; listings: ReaderListing[]}
		> = {};

		listings.forEach((listing: ReaderListing) => {
			const groupByValue = getGroupByValue(listing);
			listing.groupByValue = groupByValue;
			if (!groupsIndex[groupByValue.label]) {
				const sort = Array.isArray(listing.groupByValue.sort)
					? groupSortMap[listing.groupByValue.sort[0]] || 99
					: listing.groupByValue.sort;
				groupsIndex[groupByValue.label] = {
					label: listing.groupByValue.label,
					sort,
					listings: []
				};
			}

			groupsIndex[groupByValue.label].listings.push(listing);
		});

		listings.sortByGroups(sortOrder);
		Object.values(groupsIndex).forEach((group) => {
			group.listings.sort((a, b) => sortByGroupByValue(a.groupByValue, b.groupByValue, sortOrder));
		});

		return Object.values(groupsIndex).sort((a, b) => sortByGroupByValue(a, b, sortOrder));
	}

	function getGroupByValue(listing: ReaderListing) {
		const date = new Date(listing.item.publishedAt);
		const sectionValue = readerEdition.getSection(listing);

		if (readerEdition instanceof ContentSourceEdition) {
			const locale = LANGUAGE_TO_LOCALE[getLanguage()] || 'en-GB';
			const dateString = date.toLocaleDateString(locale, {
				weekday: 'long',
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				timeZone: readerEdition.contentSource.attributes.timezone || 'Europe/Copenhagen'
			});

			return {
				label: dateString,
				sort: date.valueOf()
			};
		}

		if (readerEdition.editionType === 'front-page-edition') {
			return {
				label: listing.item.groupBy,
				sort: listing.item.sortOrder
			};
		}

		const label = listing.item.groupBy || sectionMap[sectionValue] || sectionValue || '';

		return {
			label,
			sort: listing.item.sortOrder
		};
	}
}
